import React from 'react';
import { useDemo } from '../../contexts/DemoContext';
import { Dialog } from './Dialog';
import { fieldGuideItems } from '../../data/storySteps';
import { purrAnchorLine } from '../../data/prequel';
import { clues, echoLines, echoTitle, seedOptions } from '../../data/savanna';
import { signalCheckCards, wateringMapFields, privacyCopy } from '../../data/tools';
import type { FieldGuideItemId, ScreenId, ToolSelections, ToolStatus } from '../../types/demo';
import type { ToolField } from '../../data/tools';
export function FieldGuideDrawer() {
  const {
    state,
    dispatch,
    dialog,
    closeDialog
  } = useDemo();
  const selections = state.fieldGuideSelections;
  const openTool = (screen: ScreenId) => {
    closeDialog();
    dispatch({
      type: 'GO',
      screen
    });
  };
  const renderTool = (fields: ToolField[], values: ToolSelections, status: ToolStatus, screen: ScreenId) => <div data-id="element-411" data-mp="as1ubsnf6b63">
      <p className="text-sm text-ink/80" data-id="element-412" data-mp="5r5qtg6zcr17">
        {status === 'kept' ? 'Kept for this visit.' : status === 'blank' ? 'Left blank. Blank is a valid choice. The tool is still here.' : 'Not kept yet. You can open it any time.'}
      </p>
      <dl className="mt-2 flex flex-col gap-2" data-id="element-413" data-mp="29cr4t88erzvc">
        {fields.map(field => <div key={field.id} data-id="element-414" data-mp="2eakbe2dmivw5">
            <dt className="text-sm font-bold text-teal" data-id="element-415" data-mp="2j8u66bvfruh8">{field.question}</dt>
            <dd data-id="element-416" data-mp="2j6rja0pkyhgq">{values[field.id] ?? 'Blank'}</dd>
          </div>)}
      </dl>
      {state.currentScreen !== screen && <button type="button" onClick={() => openTool(screen)} className="mt-3 inline-flex min-h-[44px] items-center rounded-full border border-line px-4 text-sm font-bold text-forest transition-colors duration-150 hover:bg-sand hc-strong" data-id="element-417" data-mp="1qjx0r9cbps5w">
          Open this tool
        </button>}
    </div>;
  const renderItem = (id: FieldGuideItemId) => {
    switch (id) {
      case 'purrAnchor':
        return <>
            <p data-id="element-418" data-mp="17rm25ropcgdl">“{purrAnchorLine}”</p>
            <p className="mt-1 text-sm text-ink/80" data-id="element-419" data-mp="zshdpb2fpdh5">From SixBefore: Amara’s cat’s purr.</p>
          </>;
      case 'seed':
        {
          const seed = seedOptions.find(option => option.id === state.seed);
          return <p data-id="element-420" data-mp="19hhgl5k24t0m">
            {!seed || seed.id === 'blank' ? 'Planted with nothing added. Small starts still count.' : `Planted with: ${seed.label}. Small starts still count.`}
          </p>;
        }
      case 'clues':
        {
          const viewed = clues.filter(clue => state.viewedClues.includes(clue.id));
          if (viewed.length === 0) return <p data-id="element-421" data-mp="qsud2rhj4bp5">No clues opened yet. That is allowed.</p>;
          return <ul className="flex flex-col gap-2" data-id="element-422" data-mp="qpyfj49lfp18">
            {viewed.map(clue => <li key={clue.id} data-id="element-423" data-mp="lvazltsno2y9">
                <span className="font-bold text-forest" data-id="element-424" data-mp="2277x1l95w6zw">{clue.name}: </span>
                {clue.observation}
              </li>)}
          </ul>;
        }
      case 'signalCheck':
        return renderTool(signalCheckCards, selections.signalCheck, selections.signalCheckStatus, 'signalCheck');
      case 'wateringMap':
        return renderTool(wateringMapFields, selections.wateringMap, selections.wateringMapStatus, 'wateringMap');
      case 'echo':
        return <>
            <p className="font-bold text-forest" data-id="element-425" data-mp="3b3wlp6lc4lfn">{echoTitle}</p>
            <ul className="mt-1" data-id="element-426" data-mp="3b6iv7brsdb0i">
              {echoLines.map(line => <li key={line} data-id="element-427" data-mp="2p4t3wt4bqxq">{line}</li>)}
            </ul>
          </>;
      default:
        return null;
    }
  };
  return <Dialog open={dialog === 'fieldGuide'} onClose={closeDialog} id="field-guide" title="Field Guide" variant="drawer" data-id="element-428" data-mp="3p4fqm4ui7bdl">
      <p className="text-ink/80" data-id="element-429" data-mp="3u2h9oprxjap2">Things you have picked up on this visit. No points. Nothing to earn.</p>
      <ul className="mt-5 flex flex-col gap-3" data-id="element-430" data-mp="lmfrlf6xegc4">
        {fieldGuideItems.map(item => {
        const reached = state.unlockedItems.includes(item.id);
        return <li key={item.id} className={`rounded-xl border border-line p-4 hc-strong ${reached ? 'bg-cream' : 'bg-paper'}`} data-id="element-431" data-mp="2xya5v4zjcwgz">
              <h3 className="font-display text-lg font-semibold text-forest" data-id="element-432" data-mp="2sxmjwxq5z5tm">{item.title}</h3>
              <div className="mt-1" data-id="element-433" data-mp="2oy3wnvaveinm">{reached ? renderItem(item.id) : <p className="text-ink/80" data-id="element-434" data-mp="2oyx3sxdqra6a">Not reached yet.</p>}</div>
            </li>;
      })}
      </ul>
      <p className="mt-5 rounded-xl bg-sand p-4 text-sm" data-id="element-435" data-mp="3go0cqqgkp8p9">{privacyCopy}</p>
    </Dialog>;
}